import React from "react";
import { Link } from "react-router-dom";

import Icon, { IconName } from "../icons";
import s from "./ui.module.scss";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md";

const VARIANT: Record<ButtonVariant, string> = {
  primary: s.btnPrimary,
  secondary: s.btnSecondary,
  ghost: s.btnGhost,
  danger: s.btnDanger,
};
const SIZE: Record<ButtonSize, string> = { sm: s.btnSm, md: s.btnMd };

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: IconName;
  block?: boolean;
  /** Show a spinner and disable the button (e.g. while submitting). */
  loading?: boolean;
  className?: string;
  children?: React.ReactNode;
}

type ButtonProps = CommonProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "className"> & {
    /** When set, renders a react-router <Link> instead of a <button>. */
    to?: string;
  };

const Button = ({
  variant = "primary",
  size = "md",
  leftIcon,
  block,
  loading,
  className,
  children,
  to,
  type = "button",
  disabled,
  ...rest
}: ButtonProps) => {
  const cls = [s.btn, VARIANT[variant], SIZE[size], block && s.btnBlock, className]
    .filter(Boolean)
    .join(" ");

  const inner = (
    <>
      {loading ? (
        <span className={s.btnSpinner} aria-hidden="true" />
      ) : (
        leftIcon && <Icon name={leftIcon} size={size === "sm" ? 14 : 16} />
      )}
      {children}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={cls} onClick={rest.onClick as any}>
        {inner}
      </Link>
    );
  }

  return (
    // eslint-disable-next-line react/button-has-type
    <button type={type} className={cls} disabled={disabled || loading} aria-busy={loading} {...rest}>
      {inner}
    </button>
  );
};

export default Button;
